import { useState } from "react"
import { format } from "date-fns"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modules/shared/ui/components/alert-dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { ResourceAssignmentDto } from "../../dtos/ResourceAccessDtos"

interface AssignmentsTableProps {
  assignments: ResourceAssignmentDto[]
  isLoading: boolean
  onRevoke: (userId: string) => void
  isRevoking: boolean
}

export function AssignmentsTable({
  assignments,
  isLoading,
  onRevoke,
  isRevoking,
}: AssignmentsTableProps) {
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (assignments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        No one is assigned yet — this record is visible to the whole organization.
      </p>
    )
  }

  return (
    <>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Team member</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Access</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Assigned</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {assignments.map((a) => (
              <tr key={a.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-3 py-2">
                  <div className="font-medium">{a.userName}</div>
                  {a.userEmail && <div className="text-muted-foreground text-xs">{a.userEmail}</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge variant={a.level === "Edit" ? "default" : "outline"}>{a.level}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {format(new Date(a.assignedAt), "MMM d, yyyy")}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setConfirmUserId(a.userId)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmUserId} onOpenChange={() => setConfirmUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              This team member will immediately lose access to this record, unless the record ends
              up with no assignees left, in which case it becomes visible to the whole organization
              again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isRevoking}
              onClick={() => {
                if (confirmUserId) {
                  onRevoke(confirmUserId)
                  setConfirmUserId(null)
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
